import { useState, useEffect, useCallback } from 'react';

interface UseLocalStorageStateOptions<T> {
  defaultValue: T;
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
}

/**
 * 自定义 useLocalStorageState Hook
 * 支持 localStorage 持久化和 storage 事件监听
 */
export function useLocalStorageState<T>(
  key: string,
  options: UseLocalStorageStateOptions<T>
) {
  const { defaultValue, serialize = JSON.stringify, deserialize = JSON.parse } = options;

  // 获取初始值
  const getInitialValue = useCallback((): T => {
    if (typeof window === 'undefined') {
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return deserialize(item);
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  }, [key, defaultValue, deserialize]);

  // 状态管理
  const [state, setState] = useState<T>(getInitialValue);

  // 更新 localStorage
  const updateStorage = useCallback((value: T) => {
    try {
      const serializedValue = serialize(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, serialize]);

  // 监听 storage 事件（多标签页同步）
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          const newValue = deserialize(e.newValue);
          setState(newValue);
        } catch (error) {
          console.warn(`Error deserializing localStorage key "${key}":`, error);
        }
      }
    };

    // 监听 storage 事件
    window.addEventListener('storage', handleStorageChange);

    // 清理事件监听器
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key, deserialize]);

  // 设置状态的方法
  const setValue = useCallback((value: T | ((prevValue: T) => T)) => {
    const newValue = typeof value === 'function' ? (value as (prevValue: T) => T)(state) : value;
    
    setState(newValue);
    updateStorage(newValue);
  }, [state, updateStorage]);

  // 重置为默认值
  const resetValue = useCallback(() => {
    setState(defaultValue);
    updateStorage(defaultValue);
  }, [defaultValue, updateStorage]);

  // 移除 localStorage 项
  const removeValue = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setState(defaultValue);
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, defaultValue]);

  return [state, setValue, { resetValue, removeValue }] as const;
}

// 导出类型
export type UseLocalStorageStateReturn<T> = ReturnType<typeof useLocalStorageState<T>>;
